import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { X } from 'lucide-react';
import Header from '../components/Header';
import TrustBar from '../components/TrustBar';
import Footer from '../components/Footer';
import { ScrollTop, scrollToId } from '../components/egypt/EgyptHero';
import EgyptProductCard from '../components/egypt/EgyptProductCard';
import EgyptFilterBar, { MobileToursBars, FiltersButton } from '../components/egypt/EgyptFilterBar';
import HolidayFilterPanel from '../components/holidays/HolidayFilterPanel';
import ThemeChips from '../components/holidays/ThemeChips';
import MobileFilterSheet from '../components/holidays/MobileFilterSheet';
import { usePageLead } from '../components/egypt/LeadModalProvider';
import EgyptPlanner from '../components/egypt/EgyptPlanner';
import EgyptCustomerReviews from '../components/egypt/EgyptCustomerReviews';
import { buildDestinationReviews } from '../reviews/dynamicReviews';
import { EgyptFaq } from '../components/egypt/EgyptSections';
import { ChevronRight, ChevronDown } from '../components/egypt/EgyptIcons';
import { hero as egHero, tours as egTours, products as egProducts, sorts, styleBySlug, holidaysPath as egHolidaysPath, holidaysCrumbs as egCrumbs, faq as egFaq, planner as egPlanner, customerReviews as egCustomerReviews } from '../egyptListingData';
import { GROUPS, MOBILE_GROUPS, GROUP_LABELS, applyFilters, buildFacets, toggleIn, activeCount, emptySelection, facetLabel, isExperience } from '../holidays/filterModel';

const egyptHolidays = { hero: egHero, tours: egTours, products: egProducts, holidaysPath: egHolidaysPath, holidaysCrumbs: egCrumbs, faq: egFaq, planner: egPlanner, plannerSource: 'egypt-holidays', testId: 'egypt-holidays-page', customerReviews: egCustomerReviews };

const Chip = ({ label, value, onClear, testId }) => (
  <span className="inline-flex items-center gap-1 rounded-lg border border-[#C4CBD0] bg-white pl-3 pr-1.5 py-1 eg-label-lg text-[#002131]" data-testid={testId}>
    <span className="text-[#174358] font-normal">{label}</span> {value}
    <button type="button" onClick={onClear} aria-label={`Clear ${label} ${value}`} className="ml-1 w-6 h-6 rounded-full flex items-center justify-center hover:bg-black/5" data-testid={`${testId}-clear`}><X size={16} /></button>
  </span>
);

const useIsMobile = () => {
  const [m, setM] = useState(() => window.matchMedia('(max-width: 904px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 904px)');
    const fn = (e) => setM(e.matches);
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);
  return m;
};

const sortFns = {
  'price-asc': (a, b) => a.price - b.price,
  'price-desc': (a, b) => b.price - a.price,
  'days-asc': (a, b) => a.days - b.days,
  'days-desc': (a, b) => b.days - a.days
};

const HeroMedia = ({ video, webm, image, poster }) => {
  if (typeof video === 'string') {
    return (
      <video className="absolute inset-0 w-full h-full object-cover" poster={poster || image} autoPlay muted loop playsInline preload="metadata" aria-hidden="true" data-testid="eg-holidays-video-file">
        {webm && <source src={webm} type="video/webm" />}
        <source src={video} type="video/mp4" />
      </video>
    );
  }
  return (
    <>
      <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover" aria-hidden="true" />
      {video && <iframe
        src={`https://player.vimeo.com/video/${video.vimeoId}?h=${video.h}&background=1&autoplay=1&loop=1&muted=1&controls=0&title=0&byline=0&portrait=0&playsinline=1&dnt=1`}
        title={video.label}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none w-[177.78vh] min-w-full h-[56.25vw] min-h-full"
        allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
        referrerPolicy="strict-origin-when-cross-origin"
        data-testid="eg-holidays-video-iframe"
      />}
    </>
  );
};

const VideoHero = ({ video, webm, image, poster, title, count }) => (
  <div className="relative">
    <Header overlay />
    <section className="relative h-[520px] sm:h-[560px] md:h-[600px] lg:h-[640px] overflow-hidden bg-[#002131]" data-testid="eg-holidays-video">
      <HeroMedia video={video} webm={webm} image={image} poster={poster} />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,33,49,0.55)_0%,rgba(0,33,49,0.2)_45%,rgba(0,33,49,0.6)_100%)] pointer-events-none" />
      <div className="absolute inset-0 pt-[112px] md:pt-[108px] flex flex-col items-center justify-center gap-4 md:gap-5 px-4 text-center" data-testid="eg-hero-copy">
        <h1 className="eg-display-lg text-white drop-shadow-[0_2px_12px_rgba(0,33,49,0.45)] [text-wrap:balance] eg-rise" data-testid="eg-hero-title">{title}</h1>
        <button type="button" onClick={() => scrollToId('tours')} className="eg-btn-filled h-12 px-6 eg-title-md eg-rise !bg-none !bg-white !text-[#002131] hover:!bg-[#FBEADB] transition-colors" style={{ animationDelay: '120ms' }} data-testid="eg-hero-cta">{count}</button>
      </div>
    </section>
    <div data-testid="eg-trust-bar"><TrustBar happy /></div>
  </div>
);

const split = (v) => (v ? v.split(',').filter(Boolean) : []);
// Card entrance stagger plays on the first listing opened in a page load, then stops: a first
// impression rather than a delay on every page.
const risenPaths = new Set();
const WIDE = 'w-full px-4 sm:px-8 lg:px-10 2xl:px-14';

export default function EgyptHolidays({ d = egyptHolidays }) {
  const { hero, tours, products, holidaysPath, holidaysCrumbs, faq, planner, mode = 'holidays' } = d;
  const country = (planner?.h3 || '').replace(/^Customize your\s+/i, '').replace(/\s+trip$/i, '') || 'Egypt';
  const liveReviews = useMemo(() => buildDestinationReviews(country), [country]);
  const noun = mode === 'honeymoon' ? 'honeymoon' : 'holiday';
  usePageLead(holidaysCrumbs[1].label, hero.image, hero.imageAlt);
  const navigate = useNavigate();
  const { style: styleSlug } = useParams();
  const [params] = useSearchParams();
  const style = styleSlug ? (styleBySlug(styleSlug) || {}).key || null : null;
  const sort = sorts.some((s) => s.key === params.get('sort')) ? params.get('sort') : null;
  const sel = useMemo(() => ({
    theme: [...new Set([...(style && !isExperience(style) ? [style] : []), ...split(params.get('theme'))])],
    experience: [...new Set([...(style && isExperience(style) ? [style] : []), ...split(params.get('experience'))])],
    country: split(params.get('country')),
    place: split(params.get('place')),
    days: split(params.get('days')),
    price: split(params.get('price'))
  }), [params, style]);

  const go = (next, nextSort) => {
    const q = new URLSearchParams();
    GROUPS.forEach((g) => { if (next[g].length) q.set(g, next[g].join(',')); });
    if (nextSort) q.set('sort', nextSort);
    const s = q.toString();
    navigate(`${holidaysPath}${s ? `?${s}` : ''}`);
  };
  const setSel = (next) => go(next, sort);
  const toggle = (g, v) => setSel(toggleIn(sel, g, v));
  const setSort = (k) => go(sel, k);
  const clearAll = () => go(emptySelection(), sort);
  const nActive = activeCount(sel);
  const picked = [...sel.theme, ...sel.experience];
  const title = picked.length === 1 ? tours.styleH2(picked[0]) : tours.allH2;

  useEffect(() => {
    if (styleSlug && !styleBySlug(styleSlug)) navigate(holidaysPath, { replace: true });
  }, [styleSlug, navigate, holidaysPath]);
  useEffect(() => { document.title = `${title} | Hi Tours`; }, [title]);
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [rise] = useState(() => { if (risenPaths.size && !risenPaths.has(holidaysPath)) return false; risenPaths.add(holidaysPath); return true; });

  const facets = useMemo(() => buildFacets(products, sel, mode), [products, sel, mode]);
  const list = useMemo(() => {
    const f = applyFilters(products, sel, mode);
    return sort ? [...f].sort(sortFns[sort]) : f;
  }, [sel, sort, products, mode]);
  const mobile = useIsMobile();
  const [sheet, setSheet] = useState(null);
  const page = mobile ? 3 : 6;
  const [count, setCount] = useState(page);
  const [loading, setLoading] = useState(false);
  const sentinel = useRef(null);
  useEffect(() => { setCount(page); }, [sel, sort, page]);
  useEffect(() => {
    if (!sentinel.current || count >= list.length) return undefined;
    const io = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting || loading) return;
      setLoading(true);
      setTimeout(() => { setCount((c) => Math.min(c + page, list.length)); setLoading(false); }, 450);
    }, { rootMargin: '200px 0px' });
    io.observe(sentinel.current);
    return () => io.disconnect();
  }, [count, list.length, loading, page]);
  const shown = list.slice(0, count);
  const sortLabel = sort && sorts.find((s) => s.key === sort).label;
  const allCrumbs = style ? [...holidaysCrumbs.slice(0, 2), { label: holidaysCrumbs[2].label, to: holidaysPath }, { label: style }] : holidaysCrumbs;
  // Country root links now redirect straight to this listing, so drop that redundant crumb.
  const crumbs = allCrumbs.filter((c) => !(c.to && `${c.to}/holidays` === holidaysPath && c.to.split('/').length > 2));
  const resultLabel = (n) => `${n} ${noun}${n === 1 ? '' : 's'}`;
  const sortPills = { sort, onSort: setSort, onStyle: () => {}, menuKeys: ['sort'] };

  return (
    <div className="eg" data-testid={d.testId}>
      <main>
        <VideoHero video={hero.video} webm={hero.webm} image={hero.image} poster={hero.poster} title={style ? hero.styleH1(style) : hero.holidaysH1} count={tours.browse(list.length)} />
        <div className={`${WIDE} mt-6 md:mt-8`}>
          <nav className="hidden sm:flex items-center gap-1 eg-body-md text-[#174358]" aria-label="Breadcrumb" data-testid="eg-breadcrumb">
            {crumbs.map((c, i) => (
              <React.Fragment key={c.label}>
                {i > 0 && <ChevronRight size={20} className="text-[#174358]" />}
                {c.to ? <Link to={c.to} className="hover:underline">{c.label}</Link> : <span className="eg-label-lg text-[#002131]">{c.label}</span>}
              </React.Fragment>
            ))}
          </nav>
        </div>

        <section className={`${WIDE} mt-2 md:mt-4 scroll-mt-20`} id="tours" data-testid="eg-tours">
          <EgyptFilterBar title={title} cta={hero} {...sortPills} extra={<span className="eg-body-lg text-[#174358] whitespace-nowrap" data-testid="eg-result-count">{resultLabel(list.length)}</span>} />

          <div className="md:hidden mt-4 flex flex-col gap-3">
            <div className="flex items-end justify-between gap-3">
              <h2 className="eg-title-lg text-[#002131]" data-testid="eg-mobile-tours-heading">{title}</h2>
              <span className="eg-body-md text-[#174358] whitespace-nowrap pb-0.5" data-testid="eg-mobile-result-count">{resultLabel(list.length)}</span>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="eg-mobile-group-pills">
              <FiltersButton onClick={() => setSheet('experience')} active={nActive} testId="eg-inline-filters-button" />
              {MOBILE_GROUPS.map((g) => (
                <button key={g} type="button" onClick={() => setSheet(g)} className={`inline-flex items-center gap-1 h-10 pl-4 pr-3 rounded-full border eg-label-lg whitespace-nowrap transition-colors ${sel[g].length ? 'border-[#174358] bg-[#174358]/10 text-[#002131]' : 'border-[#C4CBD0] bg-white text-[#002131]'}`} data-testid={`eg-mobile-group-${g}`}>
                  {GROUP_LABELS[g]}{sel[g].length > 0 && <span className="ml-0.5 w-1.5 h-1.5 rounded-full bg-[#174358]" aria-hidden />}<ChevronDown size={18} />
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4 md:mt-2 lg:grid lg:grid-cols-[280px_minmax(0,1fr)] lg:gap-8 xl:gap-10 lg:items-start">
            <aside className="hidden lg:block sticky top-[76px] max-h-[calc(100vh-92px)] overflow-y-auto no-scrollbar rounded-2xl bg-[#F0EEE6] px-5 py-6" data-testid="eg-filter-sidebar">
              <div className="flex items-center justify-between mb-2">
                <span className="eg-title-lg text-[#002131]">Filters</span>
                {nActive > 0 && <button type="button" onClick={clearAll} className="eg-label-lg text-[#174358] underline underline-offset-2" data-testid="eg-filter-clear-all">Clear all</button>}
              </div>
              <HolidayFilterPanel facets={facets} sel={sel} onToggle={toggle} testPrefix="hf" />
            </aside>

            <div className="min-w-0">
              <div className="hidden md:block"><ThemeChips items={facets.experience} selected={sel.experience} total={applyFilters(products, { ...sel, experience: [] }, mode).length} onToggle={toggle} onClear={() => setSel({ ...sel, experience: [] })} /></div>
              {(nActive > 0 || sort) && (
                <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="eg-active-filters">
                  {GROUPS.flatMap((g) => sel[g].map((v) => <Chip key={`${g}-${v}`} label={`${GROUP_LABELS[g]}:`} value={facetLabel(g, v)} onClear={() => toggle(g, v)} testId={`eg-filter-chip-${g}`} />))}
                  {sort && <Chip label={tours.sortLabel} value={sortLabel} onClear={() => setSort(null)} testId="eg-filter-chip-sort" />}
                  {nActive > 1 && <button type="button" onClick={clearAll} className="eg-label-lg text-[#174358] underline underline-offset-2 px-1" data-testid="eg-active-clear-all">Clear all</button>}
                </div>
              )}
              {list.length ? (
                <div className="mt-6 md:mt-5 grid gap-6 sm:grid-cols-2 min-[1500px]:grid-cols-3" data-testid="eg-product-grid">
                  {shown.map((p, i) => <div key={p.slug} className={rise && i < page ? 'eg-rise' : ''} style={rise && i < page ? { animationDelay: `${120 + i * 45}ms` } : undefined}><EgyptProductCard p={p} index={i} /></div>)}
                </div>
              ) : (
                <div className="mt-8 flex flex-col items-start gap-4" data-testid="eg-empty">
                  <p className="eg-body-lg text-[#174358]">No {noun}s match these filters yet.</p>
                  <button type="button" onClick={clearAll} className="eg-btn-outlined eg-label-lg" data-testid="eg-empty-clear">Clear all filters</button>
                </div>
              )}
              {count < list.length && (
                <div ref={sentinel} className="mt-6 flex justify-center h-10" data-testid="eg-lazy-sentinel">
                  {loading && <span className="w-8 h-8 rounded-full border-[3px] border-[#E4E3DB] border-t-[#174358] animate-spin" data-testid="eg-lazy-spinner" aria-label="Loading more" />}
                </div>
              )}
            </div>
          </div>
        </section>

        <div id="planner" className="scroll-mt-20"><EgyptPlanner data={planner} source={d.plannerSource} quotes={liveReviews.items} /></div>
        <EgyptCustomerReviews data={d.customerReviews} destination={country} />
        <EgyptFaq centered className="eg-container mt-12 md:mt-16 mb-16" data={faq} />
      </main>
      <ScrollTop className="bottom-40 left-4 md:bottom-10 md:left-12" />
      <MobileToursBars title={title} cta={hero} {...sortPills} onOpenFilters={() => setSheet('experience')} activeCount={nActive} />
      <MobileFilterSheet open={!!sheet} onOpenChange={(o) => setSheet(o ? sheet : null)} initialGroup={sheet === true ? 'experience' : (sheet || 'experience')} products={products} sel={sel} mode={mode} onApply={setSel} countLabel={(n) => (n ? `Show ${resultLabel(n)}` : 'No matches')} />
      <Footer />
    </div>
  );
}
